import APIService from "../components/app/services/APIService";

const BASE_URL = "/api/v1/demo/admin/full-access";

/**
 * Frontend API service for managing full-access event demo seats.
 * All endpoints require platform_admin role.
 */
const FullAccessDemoService = {
  // ========== Seat Operations ==========

  /**
   * Provision a single full-access demo user. The backend auto-increments the
   * seat code from the highest existing demo-user-N (e.g. demo-user-6 after
   * demo-user-5). The new seat is created ACTIVE and usable immediately.
   * @param {string} eventName - Optional label for the seat (defaults to "Demo Users")
   * @param {string} expiresAt - ISO datetime string (required)
   * @param {string} [startsAt] - ISO datetime string (optional, defaults to now)
   * @returns {Promise<{success, message, seat: SeatProvisionResult}>}
   */
  createSeat: (eventName, expiresAt, startsAt = null) =>
    APIService.post(`${BASE_URL}/seat/create`, {
      eventName,
      expiresAt,
      ...(startsAt ? { startsAt } : {}),
    }),

  /**
   * Deactivate a single seat (status → DEACTIVATED). Reversible — no data deleted.
   * @param {string} seatCode
   */
  deactivateSeat: (seatCode) =>
    APIService.post(`${BASE_URL}/seat/deactivate`, { seatCode }),

  /**
   * Reactivate a previously deactivated seat (status → ACTIVE).
   * @param {string} seatCode
   */
  reactivateSeat: (seatCode) =>
    APIService.post(`${BASE_URL}/seat/reactivate`, { seatCode }),

  /**
   * Extend a seat's expiry.
   * @param {string} seatCode
   * @param {string} newExpiry - ISO datetime string
   */
  extendSeat: (seatCode, newExpiry) =>
    APIService.post(`${BASE_URL}/seat/extend`, { seatCode, newExpiry }),

  /**
   * Rotate the password for a seat account.
   * @param {string} seatCode
   * @param {string} newPassword
   */
  resetPassword: (seatCode, newPassword) =>
    APIService.post(`${BASE_URL}/seat/reset-password`, {
      seatCode,
      newPassword,
    }),

  // ========== Query Operations ==========

  /**
   * List all full-access demo seats, optionally filtered by event name.
   * @param {string|null} eventName - If provided, only returns seats for that event
   * @returns {Promise<{success, count, data: EventDemoAccount[]}>}
   */
  listSeats: (eventName = null) => {
    const query = eventName
      ? `?eventName=${encodeURIComponent(eventName)}`
      : "";
    return APIService.get(`${BASE_URL}/seats${query}`);
  },
};

export default FullAccessDemoService;
